// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import AutoDeliveryModal from "@guestsupply/App/Components/Product/AutoDeliveryModal";
import handleAutoDeliveryModalData from "@guestsupply/App/Handlers/AutoDelivery/AutoDeliveryModalHandler";
import getAutoDelivery from "@guestsupply/App/Handlers/AutoDelivery/GetAutoDeliveryHandler";
import getEstimateTotals from "@guestsupply/App/Handlers/AutoDelivery/GetEstimateTotalsHandler";
import setAutoDeliveryModalErrorMessage from "@guestsupply/App/Handlers/AutoDelivery/SetAutoDeliveryModalErrorMessage";
import setAutoDeliveryModalLoading from "@guestsupply/App/Handlers/AutoDelivery/SetAutoDeliveryModalIsDataLoading";
import setAutoDeliveryModalOpen from "@guestsupply/App/Handlers/AutoDelivery/SetAutoDeliveryModalOpen";
import { xCommonLoadingOverlay } from "@guestsupply/App/Handlers/XNG/Common";
import { GetAutoDeliveryModel, GetCarriersModel, GetEstimateTotalsModel } from "@guestsupply/App/Services/AutoDelivery";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import Button from "@guestsupply/MobiusOverrides/Button/Button";
import { mergeWithCss } from "@guestsupply/XDL/Utilities/mergeWithCss";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { Link } from "@insite/mobius/index";
import { LinkProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import * as React from "react";
import { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    getAutoDelivery,
    getEstimateTotals,
    setAutoDeliveryModalOpen,
    setAutoDeliveryModalErrorMessage,
    setAutoDeliveryModalLoading,
    handleAutoDeliveryModalData,
    xCommonLoadingOverlay,
};
export interface AutoDeliveryLinkProps {
    showAutoDelivery?: boolean;
    labelOverride?: string;
    extendedStyles?: AutoDeliveryLinkStyle;
    displayAsButton?: boolean;
}

type Props = HasProductContext &
    AutoDeliveryLinkProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

export interface AutoDeliveryAndCarriersDataModel {
    autoDeliveryData?: GetAutoDeliveryModel;
    autoCarriersData?: GetCarriersModel;
}

export interface AutoDeliveryLinkStyle {
    link?: LinkProps;
    button?: ButtonPresentationProps;
}

export const autoDeliveryLinkStyle: AutoDeliveryLinkStyle = {};

const AutoDeliveryLink: FC<Props> = ({
    labelOverride,
    showAutoDelivery,
    displayAsButton = false,
    extendedStyles,
    getAutoDelivery,
    productContext: { product, productInfo },
    productContext,
    getEstimateTotals,
    setAutoDeliveryModalOpen,
    setAutoDeliveryModalErrorMessage,
    setAutoDeliveryModalLoading,
    handleAutoDeliveryModalData,
    xCommonLoadingOverlay,
    isAuthenticated,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(autoDeliveryLinkStyle, extendedStyles));

    const toasterContext = useContext(ToasterContext);

    const variantParent = productContext.product.isVariantParent;

    if (!isAuthenticated) {
        return null;
    }

    const handleLinkClick = () => {
        if (product.id) {
            setAutoDeliveryModalLoading({ isDataLoading: true });
            setAutoDeliveryModalOpen({});
            xCommonLoadingOverlay({ key: "add", value: "AutoDelivery_PLP/PDP" });
            handleAutoDeliveryModalData({
                productId: product.id,
                productContext,
                onError: (errorMessage: string) => {
                    setAutoDeliveryModalErrorMessage({
                        errorMessage: errorMessage ?? "An Error has Occured",
                    });
                    setAutoDeliveryModalLoading({ isDataLoading: false });
                    xCommonLoadingOverlay({ key: "clear", value: "AutoDelivery_PLP/PDP" });
                },
                onSuccess: () => {
                    setAutoDeliveryModalLoading({ isDataLoading: false });
                    xCommonLoadingOverlay({ key: "clear", value: "AutoDelivery_PLP/PDP" });
                },
            });
        }
    };
    return (
        <>
            {displayAsButton ? (
                <Button
                    {...styles.button}
                    variant="tertiary"
                    disabled={variantParent}
                    onClick={handleLinkClick}
                    aria-label="Auto-Delivery Link"
                >
                    {labelOverride ?? translate("Auto-Delivery")}
                </Button>
            ) : (
                <Link
                    {...styles.link}
                    disabled={variantParent}
                    onClick={handleLinkClick}
                    aria-label="Auto-Delivery Link"
                    {...otherProps}
                >
                    {labelOverride ?? translate("Auto-Delivery")}
                </Link>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(AutoDeliveryLink));
